/**
 * Principal supported by a fixed EMI (standard amortizing loan).
 * Matches Excel: EMI ₹1,00,000 · 9% · 20y → ₹1,11,14,495.
 */
export function loanFromEmi(monthlyEmi, annualInterestRatePct, tenureYears) {
  const emi = Math.max(0, Number(monthlyEmi) || 0);
  const years = Math.max(0, Number(tenureYears) || 0);
  if (emi <= 0 || years <= 0) return 0;

  const n = Math.round(years * 12);
  const annualRate = Number(annualInterestRatePct);
  if (!Number.isFinite(annualRate) || annualRate <= 0) {
    return emi * n;
  }

  const r = annualRate / 12 / 100;
  const factor = (1 + r) ** n;
  if (factor === 1) return emi * n;
  return (emi * (factor - 1)) / (r * factor);
}

function changeSign(mode) {
  const m = String(mode || '').toLowerCase();
  if (m.startsWith('increase')) return 1;
  if (m.startsWith('decrease')) return -1;
  return 0;
}

/**
 * Project income, expenses, surplus vs annual EMI using the Excel stress-tester inputs.
 *
 * - Income / expenses grow at their rates from year 0.
 * - From move-in year, a signed expense step is applied (and then grows with expense inflation).
 * - From life-event year (if enabled), signed income/expense steps apply (and then grow).
 */
export function projectHomeLoanAffordability({
  annualTakeHome,
  annualExpenses,
  incomeGrowthPct,
  expenseGrowthPct,
  moveInYears,
  postMoveExpenseChange,
  postMoveExpenseAmount,
  hasLifeEvent,
  lifeEventYear,
  lifeEventIncomeChange,
  lifeEventIncomeAmount,
  lifeEventExpenseChange,
  lifeEventExpenseAmount,
  monthlyEmi,
  interestRatePct,
  tenureYears,
  horizonYears,
}) {
  const takeHome0 = Math.max(0, Number(annualTakeHome) || 0);
  const expenses0 = Math.max(0, Number(annualExpenses) || 0);
  const incomeG = Number(incomeGrowthPct) || 0;
  const expenseG = Number(expenseGrowthPct) || 0;
  const moveYear = Math.max(0, Math.round(Number(moveInYears) || 0));
  const moveDelta =
    changeSign(postMoveExpenseChange) * Math.max(0, Number(postMoveExpenseAmount) || 0);

  const lifeOn = Boolean(hasLifeEvent);
  const lifeYear = Math.max(0, Math.round(Number(lifeEventYear) || 0));
  const lifeIncDelta = lifeOn
    ? changeSign(lifeEventIncomeChange) * Math.max(0, Number(lifeEventIncomeAmount) || 0)
    : 0;
  const lifeExpDelta = lifeOn
    ? changeSign(lifeEventExpenseChange) * Math.max(0, Number(lifeEventExpenseAmount) || 0)
    : 0;

  const emi = Math.max(0, Number(monthlyEmi) || 0);
  const rate = Number(interestRatePct) || 0;
  const tenure = Math.max(1, Math.round(Number(tenureYears) || 1));
  const horizon = Math.max(
    1,
    Math.min(40, Math.round(Number(horizonYears) || tenure)),
  );

  const loanAmount = loanFromEmi(emi, rate, tenure);
  const annualEmi = emi * 12;

  const years = [];
  for (let t = 0; t <= horizon; t += 1) {
    let income = takeHome0 * (1 + incomeG / 100) ** t;
    let expenses = expenses0 * (1 + expenseG / 100) ** t;

    if (t >= moveYear && moveDelta !== 0) {
      expenses += moveDelta * (1 + expenseG / 100) ** (t - moveYear);
    }
    if (lifeOn && t >= lifeYear) {
      if (lifeIncDelta !== 0) {
        income += lifeIncDelta * (1 + incomeG / 100) ** (t - lifeYear);
      }
      if (lifeExpDelta !== 0) {
        expenses += lifeExpDelta * (1 + expenseG / 100) ** (t - lifeYear);
      }
    }

    expenses = Math.max(0, expenses);
    const surplus = income - expenses;
    const buffer = surplus - annualEmi;
    years.push({
      year: t,
      income,
      expenses,
      surplus,
      annualEmi,
      buffer,
      coversEmi: surplus + 0.5 >= annualEmi,
    });
  }

  const firstStressYear = years.find((row) => !row.coversEmi);
  const minBuffer = years.reduce(
    (min, row) => (row.buffer < min ? row.buffer : min),
    years[0]?.buffer ?? 0,
  );

  return {
    monthlyEmi: emi,
    annualEmi,
    loanAmount,
    year0Surplus: years[0]?.surplus ?? 0,
    years,
    firstStressYear: firstStressYear ? firstStressYear.year : null,
    minBuffer,
    endSurplus: years[years.length - 1]?.surplus ?? 0,
    coversAllYears: firstStressYear == null,
  };
}
